import React, { useEffect, useState } from "react";

const API_BASE_URL = "";

const emptyFleetCard = {
  title: "",
  category: "",
  capacity: "",
  dimensions: "",
  cabins: "",
  washroom: "",
  goodFor: "",
  desc: "",
  imgUrl: "",
  type: "",
  inLuxury: true,
  inExplore: false,
};

const emptyCorporateCard = {
  title: "",
  desc: "",
  imgUrl: "",
  ctaText: "",
  link: "",
};

const getMediaUrl = (path) => {
  if (!path) {
    return "";
  }

  if (path.startsWith("http")) {
    return path;
  }

  if (path.startsWith("/uploads/")) {
    return `${API_BASE_URL}${path}`;
  }

  return path;
};

const getLocationField = (group) => {
  return group === "a" ? "mumbaiCards" : "goaCards";
};

const cloneFleetCard = (card) => ({
  ...emptyFleetCard,
  ...card,
  _id: card?._id,
  inLuxury: card?.inLuxury === true,
  inExplore: card?.inExplore === true,
});

const cloneCorporateCard = (card) => ({
  ...emptyCorporateCard,
  ...card,
});

const Experiences = () => {
  const [pages, setPages] = useState([]);
  const [selectedPageKey, setSelectedPageKey] = useState("");
  const [data, setData] = useState(null);
  const [group, setGroup] = useState("a");
  const [form, setForm] = useState(cloneFleetCard(emptyFleetCard));
  const [editorIndex, setEditorIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem("indiaboatsAdminToken");
  const isEditing = editorIndex !== null;

  const resetEditor = (pageType) => {
    setEditorIndex(null);
    setForm(
      pageType === "corporate"
        ? cloneCorporateCard(emptyCorporateCard)
        : cloneFleetCard(emptyFleetCard)
    );
  };

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/experiences`);

        if (!response.ok) {
          throw new Error("Failed to load Experiences");
        }

        const result = await response.json();

        setPages(result);

        if (result.length > 0) {
          const defaultPage =
            result.find(
              (page) => page.pageKey === "private-yacht-charter-mumbai-goa"
            ) || result[0];

          setSelectedPageKey(defaultPage.pageKey);
          setData(defaultPage);
          resetEditor(defaultPage.pageType);
        }
      } catch (error) {
        console.error("Experiences fetch error:", error);
        alert("Failed to load Experiences");
      } finally {
        setLoading(false);
      }
    };

    fetchPages();
  }, []);

  const handlePageChange = (pageKey) => {
    const selectedPage = pages.find((page) => page.pageKey === pageKey);

    setSelectedPageKey(pageKey);
    setGroup("a");

    if (selectedPage) {
      setData(selectedPage);
      resetEditor(selectedPage.pageType);
    }
  };

  const handleGroupChange = (value) => {
    setGroup(value);
    resetEditor(data?.pageType);
  };

  const handleFormChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Image upload failed");
      }

      handleFormChange("imgUrl", result.fileUrl);
    } catch (error) {
      console.error("Image upload error:", error);
      alert(error.message || "Failed to upload image");
    }
  };

  const getItemsField = (pageData = data, groupValue = group) => {
    if (!pageData) {
      return "mumbaiCards";
    }

    if (pageData.pageType === "corporate") {
      return "corporateCards";
    }

    return getLocationField(groupValue);
  };

  const getItems = (pageData = data, groupValue = group) => {
    return pageData?.[getItemsField(pageData, groupValue)] || [];
  };

  const startAdd = () => {
    resetEditor(data?.pageType);
  };

  const startEdit = (index) => {
    const items = getItems();
    setEditorIndex(index);
    setForm(
      data.pageType === "corporate"
        ? cloneCorporateCard(items[index])
        : cloneFleetCard(items[index])
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyFormToItems = (items) => {
    const nextItems = [...items];
    const nextItem =
      data.pageType === "corporate"
        ? cloneCorporateCard(form)
        : cloneFleetCard(form);

    if (isEditing) {
      nextItems[editorIndex] = nextItem;
      return nextItems;
    }

    const hasContent = Boolean(
      form.title || form.imgUrl || form.desc || form.ctaText
    );

    if (hasContent) {
      nextItems.push(nextItem);
    }

    return nextItems;
  };

  const persist = async (nextData, successMessage) => {
    const response = await fetch(
      `${API_BASE_URL}/api/experiences/${encodeURIComponent(selectedPageKey)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(nextData),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || "Failed to save Experiences");
    }

    setData(result.data);
    setPages((previous) =>
      previous.map((page) =>
        page.pageKey === selectedPageKey ? result.data : page
      )
    );
    alert(successMessage);
    return result.data;
  };

  const handleSave = async () => {
    if (!selectedPageKey || !data) {
      return;
    }

    try {
      setSaving(true);

      const itemsField = getItemsField();
      const nextData = {
        ...data,
        [itemsField]: applyFormToItems(getItems()),
      };

      const saved = await persist(nextData, "Experiences updated successfully");
      const savedItems = getItems(saved);

      if (!isEditing) {
        const addedNew = savedItems.length > getItems().length;
        if (addedNew) {
          startAdd();
        }
      } else {
        const savedItem = savedItems[editorIndex];
        setForm(
          data.pageType === "corporate"
            ? cloneCorporateCard(savedItem || emptyCorporateCard)
            : cloneFleetCard(savedItem || emptyFleetCard)
        );
      }
    } catch (error) {
      console.error("Experience save error:", error);
      alert(error.message || "Failed to save Experiences");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (index) => {
    const item = getItems()[index];
    const label = item?.title || `Card ${index + 1}`;

    if (
      !window.confirm(
        `Delete "${label}"? This will remove it from the website.`
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const itemsField = getItemsField();
      const nextItems = getItems().filter(
        (_, itemIndex) => itemIndex !== index
      );

      await persist(
        { ...data, [itemsField]: nextItems },
        "Card deleted successfully"
      );

      if (editorIndex === index) {
        startAdd();
      } else if (editorIndex !== null && editorIndex > index) {
        setEditorIndex(editorIndex - 1);
      }
    } catch (error) {
      console.error("Experience delete error:", error);
      alert(error.message || "Failed to delete card");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="cms-page">
        <div className="cms-page-loading">Loading...</div>
      </div>
    );
  }

  const isCorporate = data.pageType === "corporate";
  const isTripFleet = data.pageType === "trip-fleet";
  const items = getItems();
  const sectionALabel = isTripFleet ? "Mandwa / Alibaug" : "Luxury Fleet";
  const sectionBLabel = isTripFleet ? "Elephanta" : "Explore Fleet";
  const groupALabel = isTripFleet ? "One Way" : "Mumbai";
  const groupBLabel = isTripFleet ? "Two Way" : "Goa";

  return (
    <div className="cms-page">
      <div className="cms-page-header">
        <div>
          <h1>Experiences</h1>
          <p>
            Add a new card or update an existing one from the list below.
          </p>
        </div>

        <button
          type="button"
          className="cms-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>Select Page</h2>
          <p>Choose which experience page you want to edit.</p>
        </div>

        <div className="cms-form-grid">
        <div className="cms-form-group">
          <label>Page</label>
          <select
            value={selectedPageKey}
            onChange={(e) => handlePageChange(e.target.value)}
          >
            {pages.map((page) => (
              <option key={page.pageKey} value={page.pageKey}>
                {page.pageName}
              </option>
            ))}
          </select>
        </div>

        {!isCorporate ? (
          <div className="cms-form-group">
            <label>{isTripFleet ? "Trip Type" : "Location"}</label>
            <select
              value={group}
              onChange={(e) => handleGroupChange(e.target.value)}
            >
              <option value="a">{groupALabel}</option>
              <option value="b">{groupBLabel}</option>
            </select>
          </div>
        ) : null}
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>{isEditing ? "Update Card" : "Add Card"}</h2>
          <p>
            {isEditing
              ? `Editing card ${editorIndex + 1}. Save changes to update it.`
              : "Fill the form and save to add a new card."}
          </p>
        </div>

        <div className="cms-editor-toolbar">
          <button type="button" className="cms-upload-btn" onClick={startAdd}>
            Add New
          </button>
        </div>

        {isCorporate ? (
          <div className="cms-form-grid">
            <div className="cms-form-group">
              <label>Image</label>
              <div className="cms-upload-box">
                <div className="cms-upload-preview">
                  {form.imgUrl ? (
                    <img
                      src={getMediaUrl(form.imgUrl)}
                      alt={form.title || "Card"}
                    />
                  ) : (
                    <span>No image selected</span>
                  )}
                </div>
                <div className="cms-upload-actions">
                  <input
                    type="file"
                    id="corp-image-editor"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    className="cms-file-input"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                  />
                  <label htmlFor="corp-image-editor" className="cms-upload-btn">
                    Choose Image
                  </label>
                </div>
              </div>
            </div>

            <div className="cms-form-group">
              <label>Title</label>
              <input
                type="text"
                value={form.title || ""}
                onChange={(e) => handleFormChange("title", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Description</label>
              <textarea
                rows="4"
                value={form.desc || ""}
                onChange={(e) => handleFormChange("desc", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Button Text</label>
              <input
                type="text"
                value={form.ctaText || ""}
                onChange={(e) => handleFormChange("ctaText", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Button Link</label>
              <input
                type="text"
                value={form.link || ""}
                onChange={(e) => handleFormChange("link", e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="cms-form-grid">
            <div className="cms-form-group">
              <label>Show This Card In</label>
              <div className="cms-checkbox-row">
                <label className="cms-checkbox-option">
                  <input
                    type="checkbox"
                    className="cms-checkbox-input"
                    checked={form.inLuxury === true}
                    onChange={(e) =>
                      handleFormChange("inLuxury", e.target.checked)
                    }
                  />
                  <span>{sectionALabel}</span>
                </label>
                <label className="cms-checkbox-option">
                  <input
                    type="checkbox"
                    className="cms-checkbox-input"
                    checked={form.inExplore === true}
                    onChange={(e) =>
                      handleFormChange("inExplore", e.target.checked)
                    }
                  />
                  <span>{sectionBLabel}</span>
                </label>
              </div>
              <small>
                Select one or both. Checking both shows the same card in both
                fleets.
              </small>
            </div>

            <div className="cms-form-group">
              <label>Image</label>
              <div className="cms-upload-box">
                <div className="cms-upload-preview">
                  {form.imgUrl ? (
                    <img
                      src={getMediaUrl(form.imgUrl)}
                      alt={form.title || "Card"}
                    />
                  ) : (
                    <span>No image selected</span>
                  )}
                </div>
                <div className="cms-upload-actions">
                  <input
                    type="file"
                    id="fleet-image-editor"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    className="cms-file-input"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                  />
                  <label htmlFor="fleet-image-editor" className="cms-upload-btn">
                    Choose Image
                  </label>
                </div>
              </div>
            </div>

            <div className="cms-form-group">
              <label>Title</label>
              <input
                type="text"
                value={form.title || ""}
                onChange={(e) => handleFormChange("title", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Category</label>
              <input
                type="text"
                value={form.category || ""}
                onChange={(e) => handleFormChange("category", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Type</label>
              <input
                type="text"
                value={form.type || ""}
                onChange={(e) => handleFormChange("type", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Capacity</label>
              <input
                type="text"
                value={form.capacity || ""}
                onChange={(e) => handleFormChange("capacity", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Size / Dimensions</label>
              <input
                type="text"
                value={form.dimensions || ""}
                onChange={(e) => handleFormChange("dimensions", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Cabins</label>
              <input
                type="text"
                value={form.cabins || ""}
                onChange={(e) => handleFormChange("cabins", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Washroom</label>
              <input
                type="text"
                value={form.washroom || ""}
                onChange={(e) => handleFormChange("washroom", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Good For</label>
              <input
                type="text"
                value={form.goodFor || ""}
                onChange={(e) => handleFormChange("goodFor", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Description</label>
              <textarea
                rows="4"
                value={form.desc || ""}
                onChange={(e) => handleFormChange("desc", e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="cms-bottom-actions">
          <button
            type="button"
            className="cms-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>
            {isCorporate
              ? "Existing Corporate Cards"
              : `${group === "a" ? groupALabel : groupBLabel} Cards`}
          </h2>
          <p>Update fills the form above. Delete asks for confirmation first.</p>
        </div>

        {items.length === 0 ? (
          <p className="cms-inquiries-empty">No cards added yet.</p>
        ) : (
          <div className="cms-item-list">
            {items.map((card, index) => (
              <div
                className={`cms-item-row${
                  editorIndex === index ? " is-active" : ""
                }`}
                key={index}
              >
                <div className="cms-item-row-thumb">
                  {card.imgUrl ? (
                    <img
                      src={getMediaUrl(card.imgUrl)}
                      alt={card.title || "Card"}
                    />
                  ) : (
                    <span>No image</span>
                  )}
                </div>
                <div className="cms-item-row-info">
                  <strong>{card.title || `Card ${index + 1}`}</strong>
                  <span>
                    {isCorporate
                      ? card.ctaText || "No button text"
                      : [
                          card.inLuxury ? sectionALabel : "",
                          card.inExplore ? sectionBLabel : "",
                        ]
                          .filter(Boolean)
                          .join(" + ") || "No fleet selected"}
                  </span>
                </div>
                <div className="cms-item-row-actions">
                  <button
                    type="button"
                    className="cms-upload-btn"
                    onClick={() => startEdit(index)}
                  >
                    Update
                  </button>
                  <button
                    type="button"
                    className="cms-delete-btn"
                    onClick={() => handleDelete(index)}
                    disabled={saving}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Experiences;
